import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Circle, Clock3, SkipForward, UserRoundCheck } from "lucide-react";
import { Alert, Button, Dialog, Field, Select, Textarea, useToast } from "@cm/ui";
import { api, ApiError } from "../../../lib/api";
import { useWsQuery } from "../../../lib/hooks";

export interface GuideStep {
  key: string;
  title: string;
  minutes: number;
  path: string[];
  href: string;
  targets: { id: string; text: string; why?: string }[];
  tips: string[];
  success: string;
  permission: string;
  status: "done" | "pending" | "skipped" | "delegated";
  seen: string | null;
  delegated_to: { name: string; at: string } | null;
}

export interface GuideState {
  started: boolean;
  status: "active" | "completed" | "dismissed";
  workspace: { id: string; name: string };
  profile: any;
  event_defaults: Record<string, Record<string, string | null>>;
  steps: GuideStep[];
  current_step: string | null;
  progress: { done: number; total: number; minutes_left: number; steps_label: string };
  test: { code: string | null; started_at: string | null; channel: { name: string; phone: string | null } | null; message: string | null; wa_link: string | null; status: any };
  ai: "real" | "simulator" | "none";
  can_manage: boolean;
  permissions: string[];
  members: { id: string; name: string; email: string; role: string }[];
}

export function useGuide(opts: { poll?: number | false; enabled?: boolean } = {}) {
  return useWsQuery<GuideState>("/api/app/guide", { refetchInterval: opts.poll ?? false, enabled: opts.enabled ?? true, retry: false });
}

/** Atualiza o cache do guia com a resposta de uma ação (todas as ações devolvem o estado completo). */
export function useGuideRefresh() {
  const qc = useQueryClient();
  return (state?: any) => {
    if (state?.steps) qc.setQueriesData({ predicate: (q) => Array.isArray(q.queryKey) && q.queryKey.includes("/api/app/guide") }, (old: any) => ({ ...(old ?? {}), ...state }));
    return qc.invalidateQueries({ predicate: (q) => Array.isArray(q.queryKey) && q.queryKey.some((k) => typeof k === "string" && k.startsWith("/api/app/guide")) });
  };
}

export function StepIcon({ status, className = "h-5 w-5" }: { status: GuideStep["status"]; className?: string }) {
  if (status === "done") return <CheckCircle2 className={`${className} text-success`} aria-label="Concluído" />;
  if (status === "skipped") return <SkipForward className={`${className} text-subtle`} aria-label="Pulado" />;
  if (status === "delegated") return <UserRoundCheck className={`${className} text-info`} aria-label="Enviado para outra pessoa" />;
  return <Circle className={`${className} text-subtle`} aria-label="Pendente" />;
}

export function ProgressBar({ done, total, label }: { done: number; total: number; label?: string }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-xs text-muted">
        <span>{done} de {total} concluídos</span>
        {label && <span className="flex items-center gap-1"><Clock3 className="h-3.5 w-3.5" aria-hidden /> {label}</span>}
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-surface-muted" role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={done} aria-label="Progresso da configuração">
        <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/** "Não tenho esse acesso": envia o passo a alguém da equipe por e-mail e avisa quando ficar pronto. */
export function DelegateDialog({ step, members, onClose }: { step: GuideStep; members: GuideState["members"]; onClose: () => void }) {
  const toast = useToast();
  const refresh = useGuideRefresh();
  const [to, setTo] = React.useState(members[0]?.id ?? "");
  const [note, setNote] = React.useState("");
  const [pending, setPending] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const send = async () => {
    setPending(true);
    setErr(null);
    try {
      const r = await api.post(`/api/app/guide/steps/${step.key}/delegate`, { user_id: to, note: note.trim() || undefined });
      await refresh(r);
      toast("Passo enviado. Avisaremos quando for concluído.");
      onClose();
    } catch (e) {
      setErr((e as ApiError).message);
    } finally {
      setPending(false);
    }
  };
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title="Não tenho esse acesso"
      description={`Envie o passo "${step.title}" para quem tem o acesso. A pessoa recebe um e-mail com o link direto e o guia avisa você quando o passo for concluído.`}
      footer={<><Button variant="outline" onClick={onClose}>Cancelar</Button><Button loading={pending} disabled={!to} onClick={send}>Enviar</Button></>}
    >
      <div className="flex flex-col gap-3">
        {err && <Alert tone="danger">{err}</Alert>}
        {members.length === 0 ? (
          <Alert tone="info">Ninguém mais da equipe tem acesso a este workspace. Convide a pessoa em Configurações → Equipe e depois envie o passo.</Alert>
        ) : (
          <Field label="Enviar para" htmlFor="delegate-to">
            <Select value={to} onChange={(e) => setTo(e.target.value)}>
              {members.map((m) => <option key={m.id} value={m.id}>{m.name} · {m.email}</option>)}
            </Select>
          </Field>
        )}
        <Field label="Recado (opcional)" htmlFor="delegate-note"><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ex.: é o acesso de administrador do CRM" /></Field>
      </div>
    </Dialog>
  );
}

/** Abre o painel de dúvidas da IA (escutado pelo AssistantPanel no Shell). */
export function openAssistant(step?: string | null) {
  window.dispatchEvent(new CustomEvent("cm:assistant-open", { detail: { step: step ?? null } }));
}

const PLATFORM: Record<string, string> = { wordpress: "WordPress", elementor: "WordPress com Elementor", wix: "Wix", shopify: "Shopify", nuvemshop: "Nuvemshop", webflow: "Webflow", custom: "página própria" };
export function platformLabel(p: string | null | undefined) {
  return p ? PLATFORM[p] ?? p : "não identificada";
}
